import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { validationUtils } from '../utils'
import { ClientErrorView } from '../components/ClientErrorView'
import { PasswordCriteriaList } from '../components/PasswordCriteriaList'
import type { Client, ClientLoadErrorType } from '../types'

export const ResetPasswordView: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')
  const token = searchParams.get('token')

  const [client, setClient] = useState<Client | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({})
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [isSuccess, setIsSuccess] = useState(false)

  const passwordCriteria = validationUtils.getPasswordCriteria(password)
  const showPasswordCriteria = isPasswordFocused || password.length > 0

  useEffect(() => {
    const loginPath = clientId ? `/login?client_id=${clientId}` : '/login'

    const loadPage = async () => {
      if (!token) {
        navigate(loginPath, { replace: true })
        return
      }

      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoadingPage(false)
        return
      }

      try {
        const [clientData, emailData] = await Promise.all([
          clientApi.getClientByPublicId(clientId),
          userApi.resolveResetPasswordEmail(token),
        ])
        setClient(clientData)
        setEmail(emailData.email)
      } catch (error) {
        console.error('Error loading reset password page:', error)
        navigate(loginPath, { replace: true })
        return
      } finally {
        setIsLoadingPage(false)
      }
    }

    loadPage()
  }, [token, clientId, navigate])

  const loginPath = clientId ? `/login?client_id=${clientId}` : '/login'

  const validateForm = (): boolean => {
    const newErrors: { password?: string; confirmPassword?: string } = {}

    if (!password) {
      newErrors.password = 'Le mot de passe est requis'
    } else if (!validationUtils.isValidPassword(password)) {
      newErrors.password = 'Le mot de passe ne respecte pas toutes les exigences'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est requise'
    } else if (!validationUtils.passwordsMatch(password, confirmPassword)) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm() || !token) return

    setIsLoading(true)
    try {
      await userApi.resetPassword(token, password)
      setIsSuccess(true)
    } catch (error) {
      console.error('Reset password error:', error)
      setErrors({
        password: 'Une erreur est survenue, veuillez réessayer plus tard',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingPage) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p className="card-loading-text">Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client) {
    return null
  }

  if (isSuccess) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          {client.logo && (
            <img
              src={client.logo}
              alt={client.title}
              style={{ height: '60px', marginBottom: '1rem' }}
            />
          )}
          <h1 className="card-title">Mot de passe redéfini</h1>
          <p className="card-subtitle">
            Votre mot de passe a été mis à jour avec succès. Retournez à l'accueil pour vous
            connecter avec votre nouveau mot de passe.
          </p>
        </div>

        <Link to={loginPath} className="btn btn-primary btn-full">
          Retour à l'accueil
        </Link>
      </div>
    )
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img
            src={client.logo}
            alt={client.title}
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Redéfinir le mot de passe</h1>
        <p className="card-subtitle">Choisissez un nouveau mot de passe pour votre compte Olecio</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Adresse email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-input"
            value={email}
            readOnly
            disabled
            aria-readonly="true"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Nouveau mot de passe<span className="form-label-required" aria-hidden="true">*</span>
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className={`form-input ${errors.password ? 'shake' : ''}`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }))
            }}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
            placeholder="Votre nouveau mot de passe"
            disabled={isLoading}
            required
            aria-describedby={showPasswordCriteria ? 'password-criteria' : undefined}
          />
          {showPasswordCriteria && (
            <div id="password-criteria">
              <PasswordCriteriaList criteria={passwordCriteria} />
            </div>
          )}
          {errors.password && (
            <div className="form-error">{errors.password}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">
            Confirmer le mot de passe<span className="form-label-required" aria-hidden="true">*</span>
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={`form-input ${errors.confirmPassword ? 'shake' : ''}`}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: undefined }))
            }}
            placeholder="Répétez votre nouveau mot de passe"
            disabled={isLoading}
            required
          />
          {errors.confirmPassword && (
            <div className="form-error">{errors.confirmPassword}</div>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              Mise à jour...
            </>
          ) : (
            'Redéfinir mon mot de passe'
          )}
        </button>
      </form>

      <div className="card-footer">
        <p className="card-footer-text">
          <Link to={loginPath} className="link">
            Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  )
}
